<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { iaReportsAPI } from 'dashboard/api/iaReports';

// ── Date range ────────────────────────────────────────────────────────────────
const today = new Date();
const defaultFrom = new Date(today);
defaultFrom.setDate(today.getDate() - 29);

const fromDate = ref(defaultFrom.toISOString().slice(0, 10));
const toDate   = ref(today.toISOString().slice(0, 10));

// ── State ─────────────────────────────────────────────────────────────────────
const loading = ref(false);
const error   = ref(null);
const data    = ref(null);

// ── Fetch ─────────────────────────────────────────────────────────────────────
async function fetchSummary() {
  loading.value = true;
  error.value   = null;
  try {
    const res = await iaReportsAPI.getSummary({
      from: fromDate.value,
      to: toDate.value,
    });
    data.value = res.data;
  } catch (e) {
    error.value = e?.response?.data?.error || e.message || 'Erro ao carregar dados';
  } finally {
    loading.value = false;
  }
}

onMounted(fetchSummary);
watch([fromDate, toDate], fetchSummary);

// ── Derived metrics ───────────────────────────────────────────────────────────
const totalConversations = computed(() => data.value?.total_conversations ?? 0);
const handoffCount       = computed(() => data.value?.handoffs?.count ?? 0);
const handoffRate        = computed(() => data.value?.handoffs?.rate ?? 0);
const avgHandoffSeconds  = computed(() => data.value?.handoffs?.avg_time_seconds ?? 0);
const aiResolutionCount  = computed(() => data.value?.ai_resolutions?.count ?? 0);
const aiResolutionRate   = computed(() => data.value?.ai_resolutions?.rate ?? 0);
const handoffsByTeam     = computed(() => data.value?.handoffs_by_team ?? []);
const timeseries         = computed(() => data.value?.handoffs_timeseries ?? []);

function formatSeconds(secs) {
  if (!secs || secs <= 0) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

const kpis = computed(() => [
  {
    label: 'Total de Conversas',
    value: totalConversations.value,
    sub: 'com sessão de IA no período',
    icon: 'i-lucide-message-square',
    color: 'text-blue-600',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    label: 'Transbordos',
    value: handoffCount.value,
    sub: `${handoffRate.value}% de taxa`,
    icon: 'i-lucide-arrow-right-left',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
  },
  {
    label: 'Tempo Médio de Transbordo',
    value: formatSeconds(avgHandoffSeconds.value),
    sub: 'do primeiro contato ao transbordo',
    icon: 'i-lucide-timer',
    color: 'text-purple-600',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
  },
  {
    label: 'Resolvidos pela IA',
    value: aiResolutionCount.value,
    sub: `${aiResolutionRate.value}% de taxa`,
    icon: 'i-lucide-bot',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
]);

// Max count for the team funnel bars
const maxTeamCount = computed(() =>
  handoffsByTeam.value.reduce((m, t) => Math.max(m, t.count), 0) || 1
);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">IA & Transbordos</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">Métricas de performance da IA e transbordos para atendimento humano</p>
      </div>

      <!-- Period filter -->
      <div class="flex items-center gap-2 bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2">
        <span class="i-lucide-calendar w-4 h-4 text-n-slate-10" />
        <input
          v-model="fromDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
        <span class="text-n-slate-9">–</span>
        <input
          v-model="toDate"
          type="date"
          class="text-sm bg-transparent text-n-slate-12 outline-none"
        />
      </div>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading" class="flex items-center justify-center py-20 text-n-slate-9">
      <span class="i-lucide-loader-2 w-6 h-6 animate-spin mr-2" />
      Carregando métricas...
    </div>

    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-400">
      <span class="i-lucide-alert-circle w-5 h-5 shrink-0" />
      <span class="text-sm">{{ error }}</span>
      <button class="ml-auto text-xs underline" @click="fetchSummary">Tentar novamente</button>
    </div>

    <template v-else-if="data">
      <!-- KPI Grid -->
      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div
          v-for="kpi in kpis"
          :key="kpi.label"
          class="flex flex-col gap-3 p-4 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm"
        >
          <div class="flex items-center justify-between">
            <p class="text-xs font-medium text-n-slate-10 uppercase tracking-wide">{{ kpi.label }}</p>
            <div :class="['p-1.5 rounded-lg', kpi.bg]">
              <span :class="[kpi.icon, 'w-4 h-4', kpi.color]" />
            </div>
          </div>
          <p class="text-2xl font-bold text-n-slate-12">{{ kpi.value }}</p>
          <p class="text-xs text-n-slate-9">{{ kpi.sub }}</p>
        </div>
      </div>

      <!-- Two column: Funnel by Team + Timeseries -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <!-- Transbordos por Time/Departamento -->
        <div class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-funnel w-4 h-4 text-n-brand" />
            Transbordos por Departamento
          </h2>

          <div v-if="handoffsByTeam.length === 0" class="py-8 text-center text-sm text-n-slate-9">
            Nenhum transbordo registrado no período.
            <p class="text-xs mt-1">Ative "Registrar Transbordos" nas configurações do agente.</p>
          </div>

          <div v-else class="flex flex-col gap-3">
            <div
              v-for="item in handoffsByTeam"
              :key="item.team"
              class="flex flex-col gap-1"
            >
              <div class="flex items-center justify-between text-xs">
                <span class="font-medium text-n-slate-11 truncate">{{ item.team || 'Sem time' }}</span>
                <span class="text-n-slate-9 shrink-0 ml-2">{{ item.count }} ({{ ((item.count / handoffCount) * 100).toFixed(1) }}%)</span>
              </div>
              <div class="w-full h-2 bg-n-alpha-3 rounded-full overflow-hidden">
                <div
                  class="h-2 bg-n-brand rounded-full transition-all duration-500"
                  :style="{ width: `${(item.count / maxTeamCount) * 100}%` }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Timeseries -->
        <div class="flex flex-col gap-4 p-5 bg-white dark:bg-n-alpha-2 rounded-xl border border-n-weak shadow-sm">
          <h2 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span class="i-lucide-trending-up w-4 h-4 text-n-brand" />
            Transbordos por Dia
          </h2>

          <div v-if="timeseries.length === 0" class="py-8 text-center text-sm text-n-slate-9">
            Sem dados de série temporal no período.
          </div>

          <div v-else class="flex flex-col gap-1.5 overflow-auto max-h-64">
            <div
              v-for="point in timeseries"
              :key="point.date"
              class="flex items-center gap-3 text-xs"
            >
              <span class="w-24 shrink-0 text-n-slate-9">{{ point.date }}</span>
              <div class="flex-1 h-4 bg-n-alpha-3 rounded overflow-hidden">
                <div
                  class="h-4 bg-amber-400 rounded transition-all"
                  :style="{ width: `${Math.max((point.count / (Math.max(...timeseries.map(t => t.count)) || 1)) * 100, 4)}%` }"
                />
              </div>
              <span class="w-8 text-right text-n-slate-11 font-medium">{{ point.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Info card when no events recorded -->
      <div
        v-if="handoffCount === 0 && totalConversations > 0"
        class="flex gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-700/30"
      >
        <span class="i-lucide-info w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div class="text-sm text-blue-700 dark:text-blue-300">
          <p class="font-medium">Nenhum transbordo registrado</p>
          <p class="text-xs mt-0.5">
            Ative <strong>"Registrar Transbordos"</strong> nas configurações de cada agente (aba Operação → Registros & Análise)
            para que os eventos apareçam aqui.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>
